import {
  AssetLimitForNetAvailability,
  SiteLimitForNetAvailability,
} from '@see-power-pft-common-js/net-availability/types';
import { UK_TIMEZONE } from '@see-power-pft-common-js/utils/product-mapper';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
  NewAssetAvailabilityEntry,
  NewSiteAvailabilityEntry,
} from 'pages/Availability/common/FormDrawer/types';
import {
  Asset,
  AssetAvailability,
  AssetLimit,
  BaseAssetAvailabilityEntry,
  BaseSiteAvailabilityEntry,
  SiteAvailability,
  SiteLimit,
} from 'pages/Availability/types';
import { Timezone } from 'types/timezone';

export const PREVIEW_ENTRY_ID = -1;

export const getLatestSiteLimit = (
  siteLimits: SiteLimitForNetAvailability[] | undefined,
): SiteLimit | null => {
  if (!siteLimits || siteLimits.length === 0) {
    return null;
  }
  const latestSiteLimit = siteLimits.toSorted(
    (a, b) => +b.start - +a.start,
  )?.[0];
  return {
    start: latestSiteLimit.start,
    maxExportPower: Math.min(
      latestSiteLimit.nameplateMaxExportPower,
      latestSiteLimit.contractualMaxExportPower,
      latestSiteLimit.gridConnectionMaxExportPower,
    ),
    maxImportPower: Math.min(
      latestSiteLimit.nameplateMaxImportPower,
      latestSiteLimit.contractualMaxImportPower,
      latestSiteLimit.gridConnectionMaxImportPower,
    ),
    energyCapacity: latestSiteLimit.contractualEnergyCapacity,
  };
};

export const getLatestAssetLimit = (
  assetLimits: AssetLimitForNetAvailability[] | undefined,
): AssetLimit | null => {
  if (!assetLimits || assetLimits.length === 0) {
    return null;
  }
  const latestAssetLimit = assetLimits.toSorted(
    (a, b) => +b.start - +a.start,
  )?.[0];
  return {
    assetName: latestAssetLimit.assetName,
    start: latestAssetLimit.start,
    maxExportPower: latestAssetLimit.nameplateMaxExportPower,
    maxImportPower: latestAssetLimit.nameplateMaxImportPower,
    energyCapacity: latestAssetLimit.nameplateEnergyCapacity,
  };
};

export const getMaxSiteLimit = (
  siteLimits: SiteLimitForNetAvailability[] | undefined,
): SiteLimit | null => {
  if (!siteLimits || siteLimits.length === 0) {
    return null;
  }

  let maxExportPower = 0;
  let maxImportPower = 0;
  let energyCapacity = 0;

  siteLimits.forEach((siteLimit) => {
    const exportPower = Math.min(
      siteLimit.nameplateMaxExportPower,
      siteLimit.contractualMaxExportPower,
      siteLimit.gridConnectionMaxExportPower,
    );
    const importPower = Math.min(
      siteLimit.nameplateMaxImportPower,
      siteLimit.contractualMaxImportPower,
      siteLimit.gridConnectionMaxImportPower,
    );
    const energyCap = siteLimit.contractualEnergyCapacity;

    if (exportPower > maxExportPower) {
      maxExportPower = exportPower;
    }
    if (importPower > maxImportPower) {
      maxImportPower = importPower;
    }
    if (energyCap > energyCapacity) {
      energyCapacity = energyCap;
    }
  });

  return {
    start: new Date(
      Math.min(...siteLimits.map((limit) => new Date(limit.start).getTime())),
    ),
    maxExportPower,
    maxImportPower,
    energyCapacity,
  };
};

export const startOfDayInTimezone = (
  date: Date | string,
  timezone: Timezone = UK_TIMEZONE,
): string => {
  const dateInTimezone = formatInTimeZone(date, timezone, 'yyyy-MM-dd');
  return fromZonedTime(`${dateInTimezone} 00:00:00`, timezone).toISOString();
};

export const endOfDayInTimezone = (
  date: Date | string,
  timezone: Timezone = UK_TIMEZONE,
): string => {
  const dateInTimezone = formatInTimeZone(date, timezone, 'yyyy-MM-dd');
  return fromZonedTime(
    `${dateInTimezone} 23:59:59.999`,
    timezone,
  ).toISOString();
};

export const getExistingSiteAvailabilityFromBaseSiteAvailabilityEntry = (
  baseAvailabilityEntry: BaseSiteAvailabilityEntry,
): SiteAvailability => {
  if (
    !baseAvailabilityEntry.sourceName ||
    !baseAvailabilityEntry.sourceId ||
    !baseAvailabilityEntry.start ||
    !baseAvailabilityEntry.availabilityReason
  ) {
    throw new Error('Source name, ID and availability reason are required');
  }
  const { availabilityReason, ...values } = baseAvailabilityEntry;
  return {
    ...values,
    start: baseAvailabilityEntry.start,
    siteAvailabilityReason: availabilityReason!,
    sourceName: baseAvailabilityEntry.sourceName,
    sourceId: baseAvailabilityEntry.sourceId,
  };
};

export const getExistingAssetAvailabilityFromBaseAssetAvailabilityEntry = (
  baseAvailabilityEntry: BaseAssetAvailabilityEntry,
): AssetAvailability => {
  if (
    !baseAvailabilityEntry.sourceName ||
    !baseAvailabilityEntry.sourceId ||
    !baseAvailabilityEntry.start ||
    !baseAvailabilityEntry.availabilityReason
  ) {
    throw new Error('Source name, ID and availability reason are required');
  }
  const { availabilityReason, ...values } = baseAvailabilityEntry;
  return {
    ...values,
    start: baseAvailabilityEntry.start,
    assetAvailabilityReason: availabilityReason!,
    sourceName: baseAvailabilityEntry.sourceName,
    sourceId: baseAvailabilityEntry.sourceId,
  };
};

export const getNewSiteAvailabilityEntryFromBaseAvailabilityEntry = (
  baseAvailabilityEntry: BaseSiteAvailabilityEntry,
): NewSiteAvailabilityEntry => {
  const { availabilityReason, ...values } = baseAvailabilityEntry;
  return {
    ...values,
    startImmediately: false,
    isValidEntry: true,
    repeatDaily: false,
    isOpenEnded: baseAvailabilityEntry.end === null,
    maxImportPower: baseAvailabilityEntry.maxImportPower?.toString() ?? null,
    maxExportPower: baseAvailabilityEntry.maxExportPower?.toString() ?? null,
    energyCapacity: baseAvailabilityEntry.energyCapacity?.toString() ?? null,
    availabilityReason: baseAvailabilityEntry.availabilityReason,
  };
};

export const getAssetFormValuesFromBaseAssetAvailabilityEntry = (
  baseAssetEntry: BaseAssetAvailabilityEntry,
): NewAssetAvailabilityEntry => {
  return {
    start: baseAssetEntry.start,
    end: baseAssetEntry.end,
    startImmediately: false,
    isOpenEnded: baseAssetEntry.end === null,
    availabilityReason: baseAssetEntry.availabilityReason,
    notes: baseAssetEntry.notes ?? '',
    isValidEntry: true,
    repeatDaily: false,
    repeatEndInclusive: null,
    selectedAssets: [baseAssetEntry.assetSourceKey],
    assetInputs: {
      [baseAssetEntry.assetSourceKey]: {
        maxExportPower: baseAssetEntry.maxExportPower?.toString() ?? null,
        maxImportPower: baseAssetEntry.maxImportPower?.toString() ?? null,
        energyCapacity: baseAssetEntry.energyCapacity?.toString() ?? null,
      },
    },
  };
};

export const getBaseAvailabilityEntriesFromSiteAvailabilities = (
  siteAvailabilities: SiteAvailability[],
): BaseSiteAvailabilityEntry[] => {
  return siteAvailabilities.map((siteAvailability) => {
    const { siteAvailabilityReason, ...values } = siteAvailability;
    return {
      ...values,
      availabilityReason: siteAvailabilityReason,
    };
  });
};

export const getBaseAvailabilityEntriesFromAssetAvailabilities = (
  assetAvailabilities: AssetAvailability[],
): BaseAssetAvailabilityEntry[] => {
  return assetAvailabilities.map((assetAvailability) => {
    const { assetAvailabilityReason, asset, ...values } = assetAvailability;
    return {
      ...values,
      availabilityReason: assetAvailabilityReason,
      assetName: asset?.name ?? '',
      assetSourceKey: getSourceKey(asset!),
    };
  });
};

export const getBaseAvailabilityEntryFromNewSiteAvailabilityEntry = (
  id: number,
  newEntry: NewSiteAvailabilityEntry,
): BaseSiteAvailabilityEntry => {
  return {
    id,
    start: newEntry.start ?? '',
    end: newEntry.isOpenEnded ? null : newEntry.end,
    siteSourceKey: newEntry.siteSourceKey,
    maxExportPower:
      newEntry.maxExportPower === null ? null : +newEntry.maxExportPower,
    maxImportPower:
      newEntry.maxImportPower === null ? null : +newEntry.maxImportPower,
    energyCapacity:
      newEntry.energyCapacity === null ? null : +newEntry.energyCapacity,
    availabilityReason: newEntry.availabilityReason,
    notes: newEntry.notes,
    isOpenEnded: newEntry.isOpenEnded,
  };
};

export const getBaseAvailabilityEntriesFromNewAssetAvailabilityEntry = (
  id: number,
  newEntry: NewAssetAvailabilityEntry,
  assets: Asset[],
): BaseAssetAvailabilityEntry[] => {
  return newEntry.selectedAssets.map((sourceKey) => {
    const assetInput = newEntry.assetInputs[sourceKey];
    return {
      id,
      start: newEntry.start,
      end: newEntry.isOpenEnded ? null : newEntry.end,
      assetSourceKey: sourceKey,
      assetName: assets.find((a) => getSourceKey(a) === sourceKey)?.name ?? '',
      maxExportPower:
        assetInput.maxExportPower === null ? null : +assetInput.maxExportPower,
      maxImportPower:
        assetInput.maxImportPower === null ? null : +assetInput.maxImportPower,
      energyCapacity:
        assetInput.energyCapacity === null ? null : +assetInput.energyCapacity,
      availabilityReason: newEntry.availabilityReason,
      notes: newEntry.notes,
      isOpenEnded: newEntry.isOpenEnded,
    };
  });
};
